import React from 'react';
import { Box, Typography } from '@mui/material';
import Hero from '../components/Common/Hero';
import SearchWidget from '../components/Common/SearchWidget';
import { useThemeContext } from '../contexts/ThemeContext';

const MARQUEE_TEXT = "YOUR RIDE // YOUR RULES // NO SURGES // JUST VIBES //";

const HomePageNew: React.FC = () => {
  const { theme } = useThemeContext();
  const isDark = theme.palette.mode === 'dark';

  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: '#f8fafc',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* ── First viewport: hero headline + search widget ── */}
      <Box
        sx={{
          flex: '0 0 auto',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          px: { xs: 2, sm: 3 },
          pb: 4,
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        {/* ── Gen Alpha Brutalist Marquee Background ── */}
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%) rotate(-10deg)',
            width: '200vw',
            pointerEvents: 'none',
            zIndex: 0,
            opacity: isDark ? 0.12 : 0.06,
            display: 'flex',
            flexDirection: 'column',
            gap: '3rem',
          }}
        >
          {[...Array(3)].map((_, i) => (
            <Box
              key={i}
              sx={{
                display: 'flex',
                whiteSpace: 'nowrap',
                animation: `homeMarquee${i} ${i % 2 === 0 ? '40s' : '50s'} linear infinite ${i % 2 !== 0 ? 'reverse' : 'normal'}`,
                [`@keyframes homeMarquee${i}`]: {
                  '0%': { transform: 'translateX(0)' },
                  '100%': { transform: 'translateX(-50%)' },
                },
              }}
            >
              {[...Array(4)].map((_, j) => (
                <Typography
                  key={j}
                  variant="h1"
                  sx={{
                    fontFamily: '"Inter", sans-serif',
                    fontSize: { xs: '6rem', md: '10rem', lg: '14rem' },
                    fontWeight: 900,
                    WebkitTextStroke: `2px ${isDark ? '#ffffff' : '#0f172a'}`,
                    WebkitTextFillColor: 'transparent',
                    paddingRight: '2rem',
                    lineHeight: 1,
                  }}
                >
                  {MARQUEE_TEXT}
                </Typography>
              ))}
            </Box>
          ))}
        </Box>

        {/* Hero copy */}
        <Box sx={{ position: 'relative', zIndex: 1, width: '100%' }}>
          <Hero />
        </Box>

        {/* Search widget – appears immediately below the headline */}
        <Box
          id="search-widget-section"
          sx={{
            width: '100%',
            maxWidth: 720,
            mx: 'auto',
            mt: 2,
            scrollMarginTop: '92px',
            position: 'relative',
            zIndex: 1,
          }}
        >
          <SearchWidget />
        </Box>

        {/* Subtle trust badges */}
        <Box
          sx={{
            mt: 3,
            display: 'flex',
            alignItems: 'center',
            gap: { xs: 2, sm: 4 },
            flexWrap: 'wrap',
            justifyContent: 'center',
            position: 'relative',
            zIndex: 1,
          }}
        >
          {[
            { emoji: '🛡️', text: 'Verified drivers' },
            { emoji: '💸', text: 'Split costs' },
            { emoji: '🌿', text: 'Eco-friendly' },
            { emoji: '⚡', text: 'Instant booking' },
          ].map(({ emoji, text }) => (
            <Box
              key={text}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.75,
                px: 1.5,
                py: 0.5,
                borderRadius: '100px',
                bgcolor: 'white',
                border: '1px solid #e2e8f0',
                boxShadow: '0 1px 4px rgba(0,0,0,0.04)',
              }}
            >
              <Box sx={{ fontSize: '0.9rem' }}>{emoji}</Box>
              <Typography
                sx={{
                  fontSize: '0.78rem',
                  fontWeight: 600,
                  color: '#475569',
                  letterSpacing: '0.01em',
                  whiteSpace: 'nowrap',
                }}
              >
                {text}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>

      {/* ── Minimal how-it-works strip ── */}
      <Box
        sx={{
          borderTop: '1px solid #e2e8f0',
          bgcolor: 'white',
          py: { xs: 6, md: 8 },
          px: { xs: 3, sm: 4 },
        }}
      >
        <Box
          sx={{
            maxWidth: 900,
            mx: 'auto',
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' },
            gap: { xs: 4, sm: 2 },
          }}
        >
          {[
            {
              step: '01',
              title: 'Search',
              desc: 'Enter your origin, destination, and date to find available rides instantly.',
              color: '#10b981',
            },
            {
              step: '02',
              title: 'Book',
              desc: 'Pick a ride that fits your schedule and budget — confirm in seconds.',
              color: '#06b6d4',
            },
            {
              step: '03',
              title: 'Ride',
              desc: 'Meet your driver, travel comfortably, and rate the experience.',
              color: '#8b5cf6',
            },
          ].map(({ step, title, desc, color }) => (
            <Box
              key={step}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 1,
                p: { xs: 0, sm: 2 },
                position: 'relative',
              }}
            >
              <Typography
                sx={{
                  fontSize: '0.7rem',
                  fontWeight: 800,
                  letterSpacing: '0.12em',
                  textTransform: 'uppercase',
                  color,
                  mb: 0.5,
                }}
              >
                {step}
              </Typography>
              <Typography
                sx={{
                  fontSize: '1.25rem',
                  fontWeight: 800,
                  color: '#0f172a',
                  letterSpacing: '-0.02em',
                }}
              >
                {title}
              </Typography>
              <Typography
                sx={{
                  fontSize: '0.9rem',
                  color: '#64748b',
                  lineHeight: 1.65,
                }}
              >
                {desc}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
};

export default HomePageNew;
